import { useAeropayUser } from '@iheartjane/data-access/react-query';
import {
  CardCarousel,
  JaneGoldCard,
  OhioGoldEducation,
} from '@iheartjane/menu';
import { Flex, JaneGoldIcon, Typography } from '@iheartjane/reefer';

import { PRODUCTS_PER_PAGE_CAROUSEL } from '@jane/search/util';
import {
  useBrandSpecialsContext,
  useMenu,
  useUserPreferences,
} from '@jane/shared-ecomm/providers';
import {
  trackCashBackDetailPageTap,
  trackMenuTabTap,
} from '@jane/shared-ecomm/tracking';
import { getJwt } from '@jane/shared/auth';
import { useJaneUser } from '@jane/shared/data-access';

import type { MenuCarouselProps } from '../../MenuComponents/MenuSponsoredCarousel/MenuSponsoredCarousel';

export const MenuBrandSpecialsCarousel = ({
  mode = 'ideal-width',
  title,
}: MenuCarouselProps & { title?: string }) => {
  const {
    appInfo: { appMode },
    paths,
    store,
  } = useMenu();
  const isOhioStore = store.state === 'Ohio';
  const { brandSpecials, loadingBrandSpecials } = useBrandSpecialsContext();
  const { data: janeUser } = useJaneUser();
  const jwt = getJwt();
  const { data: aeropayData } = useAeropayUser({
    janeUserId: isOhioStore ? janeUser?.user?.id : null,
    jwt,
  });
  const { userLocation, userPreferences = {} } = useUserPreferences();

  const { city, state, name: storeName, id: storeId } = store;
  const { zipcode } = userLocation;
  const { storeFulfillmentType, storeSearchRadius, storeType } =
    userPreferences;

  const userHasLinkedBank = !!aeropayData?.bank_accounts?.length;
  const nextBreadcrumbs = [
    { title: 'Featured', url: paths.menuCategory('featured') },
  ];

  return (
    <CardCarousel
      buttonAriaLabel={`View all ${title || 'Jane Gold rewards'}`}
      cardGap={24}
      cardGapMobile={16}
      cardHeight={290}
      cardHeightMobile={260}
      cardWidth={350}
      cardWidthMobile={320}
      isLoading={loadingBrandSpecials}
      mode={mode}
      name={title || 'Jane Gold rewards'}
      beforeHeader={
        <Flex alignItems="center">
          <JaneGoldIcon />
          <Typography ml={8} as="h2" branded variant="header-bold">
            {title || 'Jane Gold rewards'}
          </Typography>
        </Flex>
      }
      buttonTo={paths.menuCategory('jane-gold')}
      buttonOnClick={() =>
        trackMenuTabTap({
          appMode,
          productLocation: 'jane-gold',
          source: '/featured',
          storeCity: city,
          storeState: state,
          storeId: String(storeId),
          storeName: storeName,
          storeCurrency: store.currency_code,
        })
      }
      subtitle={
        isOhioStore ? (
          <OhioGoldEducation mt={4} userHasLinkedBank={userHasLinkedBank} />
        ) : undefined
      }
    >
      {brandSpecials
        .slice(0, PRODUCTS_PER_PAGE_CAROUSEL)
        .map((brandSpecial, index) => (
          <CardCarousel.Card key={brandSpecial.id + brandSpecial.title}>
            <JaneGoldCard
              authenticated={janeUser?.user?.authenticated}
              brandImage={brandSpecial.product_brand.logo_url}
              brandName={brandSpecial.product_brand.name}
              breadcrumbs={nextBreadcrumbs}
              imageSrc={brandSpecial?.photo?.urls.original}
              isBundle={brandSpecial.special_type !== 'product'}
              onClick={() =>
                trackCashBackDetailPageTap({
                  brandSpecial,
                  brandDiscountRankInList: index,
                  brandName: brandSpecial.product_brand.name,
                  cityState: `${city}, ${state}`,
                  source: '/featured',
                  storeFulfillmentType,
                  storeId,
                  storeName,
                  storeCity: city,
                  storeState: state,
                  storeSearchRadius,
                  storeType,
                  zipcode,
                })
              }
              rules={brandSpecial.rules}
              specialType={brandSpecial.special_type}
              tagLabel={brandSpecial.discount_label}
              title={brandSpecial.title}
              to={paths.menuBrandSpecial({
                id: brandSpecial.id,
                title: brandSpecial.title,
              })}
            />
          </CardCarousel.Card>
        ))}
    </CardCarousel>
  );
};
